import { Button, Drawer, message, Skeleton, Spin } from 'antd'
import React, { useEffect } from 'react'
import { createRoot } from 'react-dom/client'

import { CartDrawer, useAdd } from '@api/home/detail/addCart/useAddCart'
import Flex from '@ui-pc/Flex'
import Switch from '@ui-pc/Switch'
import { getRelativeUrl, isDev, setSearchParams } from '@utils/index'
import { request } from '@utils/request'
import { Knife, Processor } from '@utils/tool'

type Props = {
  skuId?: string
  jumpUrl: string
  params?: DetailIframe['params']
  requestUrl?: string

  /**
   * 底部右侧按钮
   */
  rightButton?
  //替换footer按钮
  footer?: React.FC<any>
}

const mDomin = process.env.quickViewMobileDomin

const ref: {
  iframeUrl?: null
  currentPath: string
  iframeContainer?: ReturnType<typeof createRoot> & {
    id: string
    loadReady: boolean
    setOpen(props: Props)
    destroy()
    onClose()
    openPedding()
    rightButton: Props['rightButton']
    productData: DetailIframe
    footer?: React.ReactNode
    // onAdd: ReturnType<typeof useAdd>['onAdd']
  }
} = {
  iframeUrl: null,
  currentPath: null,
  iframeContainer: { loadReady: false } as any
}

const toMobileUrl = () => {
  const isProd = location.hostname.indexOf('www.') > -1
  if (isProd) {
    return location.origin.replace('www.', 'm.')
  }
  const isProd2 = location.hostname.indexOf('pc.') > -1
  if (isProd2) {
    return location.origin.replace('pc.', 'm.')
  }
  return `${location.protocol}//m-${location.hostname}`
}

const getQuickViewUrl = (url: string) => {
  const quickViewUrl = getRelativeUrl(url).replace('/products/', '/quickView/')
  const src = `${toMobileUrl()}${quickViewUrl}`
  const srcDev = `http://localhost:8886${quickViewUrl}`
  // const srcDev = `https://m-betayth.yitashop.com${quickViewUrl}`
  return isDev ? srcDev : src
}

const create = (jumpUrl: string, force: boolean = false) => {
  if (ref.iframeContainer?.id && !force) return
  ref.iframeContainer?.destroy?.()

  const div = document.createElement('div')
  document.body.appendChild(div)
  const root = createRoot(div)

  //ref.iframeContainer = root as any
  Object.assign(ref.iframeContainer, root)

  ref.iframeContainer.loadReady = false

  ref.iframeContainer.destroy = () => {
    root.unmount()
    div.remove()
  }

  root.render(<QVBox id={Knife.randomString(8)} url={jumpUrl} />)

  return ref.iframeContainer
}

const checkLoadReady = () => {
  return new Promise((relove, reject) => {
    const t = setInterval(() => {
      if (ref.iframeContainer.loadReady) {
        relove(true)
        clearInterval(t)
      }
    }, 100)
  })
}

const showDrawer = async (props: Props) => {
  const { jumpUrl, footer: Footer, params, rightButton, ...rest } = props

  try {
    if (!ref.iframeContainer.loadReady) {
      ref.iframeContainer.openPedding()
      await checkLoadReady()
    }
    Footer && (ref.iframeContainer.footer = <Footer />)
    ref.iframeContainer.rightButton = rightButton
    ref.iframeContainer.setOpen({ ...rest, jumpUrl, params })
  } catch (error) {
    //window.open(jumpUrl)
    openWindowForUnexpectedError(jumpUrl)
    create('/quickView', true)
  }
}

const openWindowForUnexpectedError = (jumpUrl) => {
  const url = getQuickViewUrl(jumpUrl)
  //console.log('ssss', url)
  window.open(
    url +
      setSearchParams({
        eventName: 'unexpected',
        source: 'pc',
        jumpUrl: getRelativeUrl(jumpUrl)
      }),
    '_target',
    'height=2000,width=480,top=0,left=1980,toolbar=no,menubar=no, scrollbars=no,z-look=yes,titlebar=no,depended=yes,resizable=no,location=no, status=no'
  )
  message.info(
    `Please allow access because your browser considers this url to be unsafe.`,
    7
  )
}

const scanTimer = (
  callback,
  maxTime: number = Infinity,
  freq: number = 100
) => {
  let timeCount = 0
  const t = setInterval(() => {
    timeCount += freq
    if (timeCount > maxTime) return clearInterval(t)
    callback()
  }, freq)
}

const QVBox = (props: { url: string; id: string }) => {
  const { url, id } = props

  const [loading, useLoading] = React.useState(true)

  const [error, useError] = React.useState(false)

  const [openState, setOpen] = React.useState({
    isOpen: false,
    iframeUrl: url,
    requestUrl: null,
    params: {},
    key: url
  })

  const [drawerUrl, useDrawerUrl] = React.useState(url)

  const iframeRef = React.useRef<HTMLIFrameElement>()

  //const skuRef = React.useRef<DetailIframe>({} as any)

  const refTouch = React.useRef({
    alreadyReceive: false,
    delayLoad: false,
    jumpUrl: null,
    params: null,
    requestUrl: null,
    alreadyRequest: false,
    isRealLoaded: false,
    isErrorLoaded: false
    // timer: null
  })

  const onOpen = (options: Props) => {
    const { jumpUrl, params, requestUrl } = options
    useLoading(true)
    if (refTouch.current.isErrorLoaded && !refTouch.current.isRealLoaded) {
      refTouch.current.isErrorLoaded = false
      refTouch.current.isRealLoaded = false
      setOpen({
        ...openState,
        params,
        requestUrl,
        iframeUrl: jumpUrl,
        key: Knife.randomString(8)
      })
      return
    }

    if (error) {
      setOpen({ ...openState, params, requestUrl, iframeUrl: jumpUrl })
      useDrawerUrl(jumpUrl)
      openWindowForUnexpectedError(jumpUrl)
      return
    }

    setOpen({ ...openState, requestUrl, params, isOpen: true })
    useDrawerUrl(jumpUrl)

    if (!refTouch.current.isRealLoaded) {
      refTouch.current.isErrorLoaded = true
      openWindowForUnexpectedError(jumpUrl)
      onClose()
      return
    }

    postData({ jumpUrl, requestUrl, params })

    if (!refTouch.current.alreadyReceive) {
      refTouch.current.delayLoad = true
      refTouch.current.jumpUrl = jumpUrl
      refTouch.current.params = params
      refTouch.current.requestUrl = requestUrl
    }
  }

  const onClose = () => {
    setOpen({ ...openState, isOpen: false })
    ref.iframeContainer.footer = null
  }

  Object.assign(ref.iframeContainer, {
    id,
    onClose,
    setOpen: onOpen,
    openPedding: () => {
      useLoading(true)
      setOpen({ ...openState, isOpen: true })
    }
  })

  const postData = ({ jumpUrl, params, requestUrl }) => {
    const iframe = iframeRef.current
    console.log('postdata', jumpUrl)
    refTouch.current.alreadyRequest = false
    iframe.contentWindow.postMessage(
      {
        eventName: 'quickView',
        jumpUrl: getQuickViewUrl(jumpUrl),
        params: params,
        requestUrl,
        source: 'pc'
      } as DetailIframe,
      //mDomin
      //'http://192.168.80.146:8889/'
      '*'
    )
    //检擦因为意外情况导致iframe无法打开的问题
    // refTouch.current.timer = setTimeout(() => {
    //   if (refTouch.current.alreadyRequest) return
    //   openWindowForUnexpectedError(jumpUrl)
    //   onClose()
    //   setOpen({ ...openState, key: Knife.randomString(8) })
    // }, 50000000000)
  }

  const ifSrc = getQuickViewUrl(openState.iframeUrl)
  //设置init事件
  useEffect(() => {
    const handler = (e: { data: DetailIframe }) => {
      if (e.data.eventName === 'quickViewInit') {
        if (!e.data.alreadyReceive) {
          refTouch.current.alreadyReceive = true
        }
        if (refTouch.current.delayLoad) {
          // const {} = postData({ ...refTouch.current })
        }
        console.log('quickViewInit')
      }
    }
    window.addEventListener('message', handler, false)

    return () => {
      window.removeEventListener('message', handler)
    }
  }, [])

  //获取子路由sku
  useEffect(() => {
    const handler = (e: { data: DetailIframe }) => {
      if (e.data.eventName === 'skuChange') {
        const { data } = e
        //打开购物车界面
        console.log('skuChange', data)
        //skuRef.current = data
        ref.iframeContainer.productData = data
      }
    }
    window.addEventListener('message', handler, false)
    return () => {
      window.removeEventListener('message', handler)
    }
  }, [])

  //设置载入动画
  useEffect(() => {
    const handler = (e: { data: DetailIframe }) => {
      if (e.data.eventName === 'quickViewLoaded') {
        const { data } = e
        useLoading(false)
        refTouch.current.alreadyRequest = true
        // clearTimeout(refTouch.current.timer)
        // console.log('quickViewLoaded', data.isLoading)
      }

      if (e.data.eventName === 'quickViewClose') {
        ref.iframeContainer.onClose()

        const { data } = e
        if (data?.jumpUrl) {
          const currentUrl = window?.location.pathname + window?.location.search
          const url = getRelativeUrl(data?.jumpUrl)

          if (currentUrl !== url) {
            window.location.href = url
          }
        }
      }
    }
    window.addEventListener('message', handler, false)
    return () => {
      window.removeEventListener('message', handler)
    }
  }, [])

  //载入检测
  useEffect(() => {
    const handler = (e: { data: DetailIframe }) => {
      if (e.data.eventName === 'imconnect') {
        // iframeRef.current.contentWindow.postMessage({ eventName: 'yeahim!' })
        refTouch.current.isRealLoaded = true
        refTouch.current.isErrorLoaded = false
        ref.iframeContainer.loadReady = true
      }
    }
    window.addEventListener('message', handler, false)
    return () => {
      window.removeEventListener('message', handler)
    }
  }, [])

  useEffect(() => {
    ref.iframeContainer.loadReady = false
    setTimeout(() => {
      ref.iframeContainer.loadReady = true
    }, 1000 * 20)
  }, [])

  const onLoad = () => {
    // iframeRef.current.contentWindow.postMessage({ eventName: 'hello!' })
  }

  return (
    <>
      <Drawer
        open={openState.isOpen}
        rootClassName="quickview-drawer"
        title="Product Details"
        placement="right"
        forceRender
        width="480"
        footer={
          <Spin indicator={null} spinning={loading}>
            {ref.iframeContainer.footer}
          </Spin>
        }
        zIndex={2000}
        onClose={onClose}>
        <Switch either={loading}>
          <Flex rowGap={32} padding={[32]}>
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
          </Flex>
          <Switch either={!Boolean(ref.iframeContainer.footer)}>
            <Spin spinning={loading} indicator={null}>
              <div
                onClick={() => useLoading(true)}
                className="quickview-button-area"
                style={{
                  opacity: loading ? 0 : 1
                }}>
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    window.open(drawerUrl)
                  }}>
                  View Details
                </Button>
                {ref.iframeContainer.rightButton}
              </div>
            </Spin>
          </Switch>
        </Switch>
        <iframe
          sandbox="allow-same-origin allow-scripts"
          key={openState.key}
          ref={iframeRef}
          src={ifSrc}
          frameBorder="0"
          onLoad={onLoad}
          onError={() => useError(true)}
          style={{
            width: '100%',
            height: '100%',
            opacity: loading ? 0 : 1,
            top: 0,
            left: 0
          }}></iframe>
      </Drawer>
    </>
  )
}

const useQuickView = (
  obj = {},
  updateAddCartCount?: (count: number, skuId: string) => void,
  terminal?: 'hepc' | 'hem' | 'homepc' | 'homem'
) => {
  // const { jumpUrl } = props

  const { onAdd, handler } = useAdd(null, terminal)

  const triggerRender = () => {
    create('/quickView')
  }

  const open = (props: Props) => {
    const { jumpUrl, params = {}, ...rest } = props

    const rightButton = (
      <Button
        type="primary"
        onClick={async () => {
          await onAdd({
            ...ref.iframeContainer.productData.info,
            ...ref.iframeContainer.productData,
            ...params
          } as any)

          updateAddCartCount?.(1, ref.iframeContainer.productData.skuId)

          ref.iframeContainer.onClose()
        }}>
        Add to Cart
      </Button>
    )

    showDrawer({
      ...rest,
      jumpUrl,
      params,
      rightButton: props.rightButton ? (
        <props.rightButton
          onClose={ref.iframeContainer.onClose}
          getData={() => ref.iframeContainer.productData}
        />
      ) : (
        rightButton
      )
    })
  }

  /**
   * 通过 mouseOver 预渲染
   */
  const onMouseOver = () => {
    triggerRender()
  }

  React.useEffect(() => {
    const render = () => {
      try {
        requestIdleCallback(triggerRender)
      } catch (error) {
        setTimeout(triggerRender)
      }
      window.removeEventListener('mouseover', render)
      //console.log('triggerRender')
    }
    window.addEventListener('mouseover', render)
    return () => {
      window.removeEventListener('mouseover', render)
    }
  }, [])

  return {
    open,
    close: () => ref.iframeContainer.onClose(),
    getProductData: () => ref.iframeContainer.productData,
    drawer: <CartDrawer {...handler} />
  }
}

export default useQuickView
